import { useTranslation } from 'react-i18next';
import { format, isToday, isPast, isSameMonth } from 'date-fns';
import CalendarPostCard from './CalendarPostCard';
import type { ScheduledPostEntry, CalendarView } from '../../types/calendar';

interface CalendarDayCellProps {
  /** The date this cell represents */
  date: Date;
  /** Scheduled posts for this day */
  posts: ScheduledPostEntry[];
  /** The currently selected/reference date (for month view context) */
  selectedDate: Date;
  /** Current calendar view mode */
  view: CalendarView;
  /** Handler for clicking on a post */
  onPostClick?: (post: ScheduledPostEntry) => void;
  /** Handler for clicking empty area to add a post */
  onAddPost?: (date: Date) => void;
  /** Handler for rescheduling a post */
  onReschedulePost?: (post: ScheduledPostEntry) => void;
  /** Handler for unscheduling a post */
  onUnschedulePost?: (post: ScheduledPostEntry) => void;
  /** Optional map of post IDs to titles */
  postTitles?: Record<string, string>;
}

/**
 * CalendarDayCell component
 * Displays an individual day in the calendar grid with its scheduled posts.
 * Supports different views (day/week/month) with appropriate styling.
 */
export default function CalendarDayCell({
  date,
  posts,
  selectedDate,
  view,
  onPostClick,
  onAddPost,
  onReschedulePost,
  onUnschedulePost,
  postTitles = {},
}: CalendarDayCellProps) {
  const { t } = useTranslation();

  const today = isToday(date);
  const pastDay = isPast(date) && !today;
  const currentMonth = isSameMonth(date, selectedDate);
  const dayNumber = format(date, 'd');

  /**
   * Determine if this cell should use compact display
   * Month view uses compact, week/day views use full
   */
  const isCompact = view === 'month';

  /**
   * Maximum posts to display before showing "more" indicator
   */
  const maxVisiblePosts = isCompact ? 2 : 5;
  const visiblePosts = posts.slice(0, maxVisiblePosts);
  const hiddenCount = posts.length - maxVisiblePosts;

  /**
   * Handle click on empty area of the cell
   */
  const handleCellClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on a post card
    if ((e.target as HTMLElement).closest('[data-post-card]')) {
      return;
    }
    // Don't allow adding posts to past days
    if (pastDay) {
      return;
    }
    onAddPost?.(date);
  };

  /**
   * Get cell styling based on state
   */
  const getCellClasses = (): string => {
    const baseClasses = `
      relative flex flex-col
      border-2 border-black dark:border-gray-600
      transition-all duration-150
    `;

    // View-specific sizing
    const sizeClasses = view === 'month'
      ? 'min-h-[100px]'
      : view === 'week'
        ? 'min-h-[120px]'
        : 'min-h-[80px] flex-1';

    // Today highlighting
    const todayClasses = today
      ? 'bg-yellow-50 dark:bg-yellow-900/20 ring-2 ring-yellow-400 ring-inset'
      : '';

    // Past day dimming
    const pastClasses = pastDay && !today
      ? 'bg-gray-50 dark:bg-gray-900/50'
      : 'bg-white dark:bg-gray-800';

    // Current month styling (for month view)
    const monthClasses = !currentMonth && view === 'month'
      ? 'bg-gray-100 dark:bg-gray-900/70'
      : '';

    // Hover state (only for future days)
    const hoverClasses = !pastDay
      ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
      : '';

    return `${baseClasses} ${sizeClasses} ${todayClasses} ${pastClasses} ${monthClasses} ${hoverClasses}`;
  };

  /**
   * Get day number styling
   */
  const getDayNumberClasses = (): string => {
    const baseClasses = 'font-bold text-sm';

    if (today) {
      return `${baseClasses} w-7 h-7 flex items-center justify-center rounded-full bg-black dark:bg-white text-white dark:text-black`;
    }

    if (!currentMonth && view === 'month') {
      return `${baseClasses} text-gray-400 dark:text-gray-500`;
    }

    if (pastDay) {
      return `${baseClasses} text-gray-400 dark:text-gray-500`;
    }

    return `${baseClasses} text-gray-900 dark:text-gray-100`;
  };

  return (
    <div
      className={getCellClasses()}
      onClick={handleCellClick}
      role="button"
      tabIndex={pastDay ? -1 : 0}
      aria-label={`${format(date, 'EEEE, MMMM d, yyyy')}${today ? ` (${t('calendar.today')})` : ''}${posts.length > 0 ? `, ${posts.length} ${t('calendar.posts')}` : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!pastDay) {
            onAddPost?.(date);
          }
        }
      }}
    >
      {/* Day Header */}
      <div className="flex items-start justify-between p-2">
        <span className={getDayNumberClasses()}>
          {dayNumber}
        </span>

        {/* Today badge (for week/day view) */}
        {today && view !== 'month' && (
          <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">
            {t('calendar.today')}
          </span>
        )}

        {/* Add button (visible on hover for future days) */}
        {!pastDay && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddPost?.(date);
            }}
            className="
              opacity-0 group-hover:opacity-100
              w-6 h-6 flex items-center justify-center
              rounded border border-black dark:border-gray-600
              bg-white dark:bg-gray-800
              hover:bg-gray-100 dark:hover:bg-gray-700
              transition-opacity
              text-gray-600 dark:text-gray-400
              hover:text-black dark:hover:text-white
            "
            aria-label={t('calendar.addPost')}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Posts Container */}
      <div className={`
        flex-1 px-2 pb-2 overflow-hidden
        ${isCompact ? 'space-y-1' : 'space-y-2'}
      `}>
        {posts.length === 0 ? (
          // Empty state - subtle indicator for future days
          !pastDay && view !== 'month' && (
            <div className="flex items-center justify-center h-full">
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                {t('calendar.noPostsScheduled')}
              </span>
            </div>
          )
        ) : (
          <>
            {/* Visible posts */}
            {visiblePosts.map((post) => (
              <div key={post.scheduleId} data-post-card>
                <CalendarPostCard
                  post={post}
                  title={post.post.title || undefined}
                  onClick={onPostClick}
                  onReschedule={onReschedulePost}
                  onUnschedule={onUnschedulePost}
                  compact={isCompact}
                />
              </div>
            ))}

            {/* More posts indicator */}
            {hiddenCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Could open a modal to show all posts for this day
                  // For now, clicking a visible post will open details
                }}
                className="
                  w-full text-center py-1
                  text-xs font-medium
                  text-gray-600 dark:text-gray-400
                  hover:text-black dark:hover:text-white
                  hover:underline
                  transition-colors
                "
              >
                {t('calendar.morePostsCount', { count: hiddenCount })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Past day overlay indicator */}
      {pastDay && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-2 right-2">
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
              {/* Could add "Past" label if needed */}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
